import { useAuth } from '@hostesa/auth-client';
import { useFlags } from '@hostesa/feature-flags';
import { useT } from '@hostesa/i18n';
import { openCommandPalette, useAppDispatch } from '@hostesa/state';
import { AppShell, Button, type NavGroup } from '@hostesa/ui';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { ArrowLeft, LogOut, Search } from 'lucide-react';
import { useMemo } from 'react';
import { OperatorCommandPalette } from './command-palette';
import { ImpersonationBanner } from './impersonation-banner';
import { NAV_GROUPS } from './nav-config';

/**
 * Up-to-two-letter initials for the sidebar avatar. Prefers the first letters
 * of a two-part display name; falls back to the first letters of the name or
 * email, and to "·" when neither is present.
 */
function initialsOf(name: string | null | undefined, email: string | null | undefined): string {
  const source = (name ?? email ?? '').trim();
  if (!source) return '·';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

/**
 * Operator-scoped layout: the brand AppShell + SidebarNav, with nav items gated
 * by RBAC permissions (`can()`) and feature flags. Renders the operator pages
 * via `<Outlet/>`, beneath a persistent <ImpersonationBanner/>.
 *
 * The operator gate (hostesa_admin) has already run in <OperatorGuard> before
 * this component mounts; `can()` here evaluates the operator's role.
 */
export function OperatorLayout() {
  const { t } = useT('backoffice');
  const { can, user, logout } = useAuth();
  const flags = useFlags();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  const userInitials = initialsOf(user?.fullName, user?.email);

  const navGroups = useMemo<NavGroup[]>(() => {
    return NAV_GROUPS.map((group) => ({
      id: group.id,
      label: t(group.labelKey),
      items: group.items
        // RBAC: hide items the operator role cannot use.
        .filter((item) => can(item.permission))
        // Feature flags: hide flagged-off surfaces (e.g. provider diagnostics).
        .filter((item) => (item.flag ? flags[item.flag] : true))
        .map((item) => {
          const href = `/${item.segment}`;
          const Icon = item.icon;
          return {
            id: item.id,
            label: t(item.labelKey),
            icon: <Icon aria-hidden="true" />,
            href,
            active: pathname === href || pathname.startsWith(`${href}/`),
            onClick: (() => {}) as () => void,
          };
        }),
    })).filter((group) => group.items.length > 0);
  }, [t, can, flags, pathname]);

  // Intercept nav clicks for client-side routing (hrefs keep links real for a11y).
  const navGroupsWithHandlers = useMemo<NavGroup[]>(
    () =>
      navGroups.map((group) => ({
        ...group,
        items: group.items.map((item) => ({
          ...item,
          onClick: () => {
            // `to` cast: see lib/nav.ts (platform owns the global router Register).
            if (item.href) void navigate({ to: item.href as never });
          },
        })),
      })),
    [navGroups, navigate],
  );

  return (
    <AppShell
      data-testid="backoffice-shell"
      navGroups={navGroupsWithHandlers}
      logo={
        // Prototype `.brandrow`: mark + wordmark + an OPS env badge.
        <span className="flex w-full items-center gap-2.5">
          <img src="/favicon.svg" alt="" className="size-[26px]" />
          <span className="font-[var(--font-display)] text-[15px] font-semibold text-[var(--color-rail-ink)]">
            Hostesa
          </span>
          <span className="ml-auto rounded-[5px] border border-[var(--color-rail-line)] px-[5px] py-0.5 font-[var(--font-mono)] text-[9px] uppercase tracking-[0.14em] text-[var(--color-rail-muted)]">
            {t('shell.opsBadge')}
          </span>
        </span>
      }
      topBar={{
        leading: (
          <span
            data-testid="operator-console-label"
            className="font-[var(--font-display)] text-sm font-medium text-[var(--color-charcoal)]"
          >
            {t('shell.operatorConsole')}
          </span>
        ),
        statusLine: t('shell.crossTenant'),
        trailing: (
          <Button data-testid="logout-btn" variant="ghost" size="sm" onClick={() => logout()}>
            <LogOut className="size-4" strokeWidth={1.3} aria-hidden="true" />
            {t('auth.signOut', { ns: 'common' })}
          </Button>
        ),
      }}
      sidebarHeader={
        <div className="flex flex-col gap-3">
          {/* Account/context chip — prototype `.userchip` card. */}
          <div
            data-testid="operator-context-chip"
            className="flex items-center gap-2.5 rounded-md border border-[var(--color-line)] bg-[var(--color-surface)] px-2.5 py-2 shadow-[var(--shadow-card)]"
          >
            <span
              aria-hidden="true"
              className="grid size-7 shrink-0 place-items-center rounded-full bg-[var(--color-charcoal)] font-[var(--font-display)] text-[12px] font-semibold text-[var(--color-alabaster)]"
            >
              H
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-[var(--font-display)] text-[12.5px] font-medium text-[var(--color-charcoal)]">
                {t('shell.operatorConsole')}
              </span>
              <span className="truncate font-[var(--font-mono)] text-[10.5px] text-[var(--color-stone-mid)]">
                {t('shell.internal')}
              </span>
            </span>
          </div>
          <button
            type="button"
            data-testid="operator-search"
            // Opens the operator command palette (⌘K / Ctrl+K). Mirrors the
            // platform's CommandPaletteTrigger; the palette itself is mounted
            // below as <OperatorCommandPalette/>.
            onClick={() => dispatch(openCommandPalette())}
            aria-label={t('shell.search')}
            className="flex w-full items-center gap-2 rounded-md border border-[var(--color-line)] bg-[var(--color-surface-2)] px-2.5 py-1.5 text-left text-sm text-[var(--color-stone-mid)] transition-colors hover:bg-[var(--color-canvas)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
          >
            <Search className="size-3.5" strokeWidth={1.3} aria-hidden="true" />
            <span className="flex-1 truncate">{t('shell.search')}</span>
            <kbd className="rounded border border-[var(--color-line)] px-1 font-[var(--font-mono)] text-[10px]">
              ⌘K
            </kbd>
          </button>
        </div>
      }
      sidebarFooter={
        <div className="flex flex-col gap-3">
          <a
            data-testid="back-to-platform"
            href={
              (import.meta.env.VITE_PLATFORM_BASE_URL as string | undefined) ??
              'http://localhost:5173'
            }
            className="flex items-center gap-2 text-xs text-[var(--color-stone-mid)] transition-colors hover:text-[var(--color-charcoal)]"
          >
            <ArrowLeft className="size-3.5" strokeWidth={1.3} aria-hidden="true" />
            {t('shell.backToPlatform')}
          </a>
          <div
            data-testid="sidebar-user"
            className="flex items-center gap-2.5 text-xs text-[var(--color-stone-mid)]"
          >
            <span
              data-testid="sidebar-user-avatar"
              aria-hidden="true"
              className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[var(--color-charcoal)] font-medium text-[0.7rem] text-[var(--color-alabaster)]"
            >
              {userInitials}
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate font-medium text-[var(--color-charcoal)]">
                {user?.fullName ?? user?.email}
              </span>
              <span className="truncate">{t('shell.operator')}</span>
            </span>
          </div>
        </div>
      }
    >
      <OperatorCommandPalette />
      <ImpersonationBanner />
      <Outlet />
    </AppShell>
  );
}
